import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Modal, ModalBody, Input, Label } from 'reactstrap';

/**
 * DeleteModal — destructive confirmation.
 *
 *   <DeleteModal
 *     show={open}
 *     name={member.fullName}          // optional, bolded in the message
 *     title="Delete member"           // optional
 *     requireTyping                   // optional: user must type "delete"
 *     onDeleteClick={handleDelete}
 *     onCloseClick={() => setOpen(false)}
 *   />
 *
 * Optional, for deactivate / reinstate style confirmations:
 *   variant="warning|success|danger"   tints the icon and confirm button
 *   effects={['…']}                    "What will happen" list under the message
 *   requireReason                      shows a required reason box; onDeleteClick(reason)
 *   icon="mdi mdi-…"                   confirm button icon
 */
const VARIANTS = {
  danger: { badge: 'danger', btn: 'btn-danger', icon: 'mdi mdi-alert-outline', btnIcon: 'mdi mdi-trash-can-outline' },
  warning: { badge: 'warning', btn: 'btn-warning', icon: 'mdi mdi-alert-outline', btnIcon: 'mdi mdi-account-off-outline' },
  success: { badge: 'success', btn: 'btn-success', icon: 'mdi mdi-check-circle-outline', btnIcon: 'mdi mdi-account-check-outline' },
};

const DeleteModal = ({
  id,
  show,
  name,
  title = 'Delete record',
  text,
  keyword = 'delete',
  requireTyping = false,
  confirmText = 'Delete',
  variant = 'danger',
  effects,
  requireReason = false,
  icon,
  loading = false,
  onDeleteClick,
  onCloseClick,
}) => {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (show) {
      setTyped('');
      setReason('');
    }
  }, [show]);

  const v = VARIANTS[variant] || VARIANTS.danger;
  const ready =
    (!requireTyping || typed.trim().toLowerCase() === keyword.toLowerCase()) &&
    (!requireReason || reason.trim().length > 0) &&
    !loading;
  const subject = name || (id ? `record #${id}` : 'this record');

  return (
    <Modal size="md" isOpen={show} toggle={onCloseClick} centered>
      <ModalBody className="p-4">
        <div className="d-flex align-items-center gap-3 mb-3">
          <span
            className={`rounded-circle bg-${v.badge} bg-opacity-10 text-${v.badge} d-inline-flex align-items-center justify-content-center flex-shrink-0`}
            style={{ width: 44, height: 44, fontSize: 20 }}
          >
            <i className={v.icon} />
          </span>
          <h5 className="mb-0 fw-semibold">{title}</h5>
        </div>

        <p className="text-muted mb-3">
          {text || (
            <>
              Are you sure you want to delete <strong className="text-body">{subject}</strong>?
              This action cannot be undone.
            </>
          )}
        </p>

        {effects && effects.length > 0 && (
          <div className="border rounded bg-light p-3 mb-3">
            <div className="text-uppercase text-muted fw-semibold small mb-2">What will happen</div>
            <ul className="mb-0 ps-3">
              {effects.map((item) => (
                <li key={item} className="text-muted mb-1">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {requireReason && (
          <div className="mb-3">
            <Label for="action-reason-input" className="fw-medium">
              Reason <span className="text-danger">*</span>
            </Label>
            <Input
              id="action-reason-input"
              type="textarea"
              rows={3}
              autoFocus
              placeholder="Enter a reason…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        )}

        {requireTyping && (
          <div className="mb-3">
            <Label for="delete-confirm-input" className="fw-medium">
              Type &quot;{keyword}&quot; to confirm deletion:
            </Label>
            <Input
              id="delete-confirm-input"
              autoFocus
              autoComplete="off"
              placeholder={`Type ${keyword} here`}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && ready) onDeleteClick(reason);
              }}
            />
          </div>
        )}

        <div className="d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-light" onClick={onCloseClick}>
            Cancel
          </button>
          <button
            type="button"
            className={`btn ${v.btn}`}
            disabled={!ready}
            onClick={() => onDeleteClick(reason)}
          >
            <i className={`${icon || v.btnIcon} me-1`} />
            {confirmText}
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
};

DeleteModal.propTypes = {
  id: PropTypes.any,
  show: PropTypes.any,
  name: PropTypes.string,
  title: PropTypes.string,
  text: PropTypes.node,
  keyword: PropTypes.string,
  requireTyping: PropTypes.bool,
  confirmText: PropTypes.string,
  variant: PropTypes.string,
  effects: PropTypes.arrayOf(PropTypes.string),
  requireReason: PropTypes.bool,
  icon: PropTypes.string,
  loading: PropTypes.bool,
  onCloseClick: PropTypes.func,
  onDeleteClick: PropTypes.func,
};

export default DeleteModal;
